import React from "react"
import { QuoteRequestRO, QuoteRO } from "@ub/quoting-client"
import { TrashIcon } from "lucide-react"

import { formatDate } from "@/lib/helper"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import { QuadProductImagesPreview } from "@/components/ui/components/quad-product-images-preview"

export type QuoteStatus = "EXPIRED" | "NEW" | "RESPONDED" | "REJECTED"

export function getQuoteStatus(quote: QuoteRO | QuoteRequestRO, isQuoteRequest: boolean): QuoteStatus {
  if (isQuoteRequest) {
    return "NEW"
  }

  const quoteRO = quote as QuoteRO
  if (quoteRO.rejected) {
    return "REJECTED"
  }

  if (quoteRO.validToDate && new Date(quoteRO.validToDate) < new Date()) {
    return "EXPIRED"
  }

  return "RESPONDED"
}
export function getStatusColor(status: QuoteStatus): string {
  switch (status) {
    case "EXPIRED":
      return "text-gray-600"
    case "NEW":
      return "text-blue-600"
    case "RESPONDED":
      return "text-green-600"
    case "REJECTED":
      return "text-red-600"
    default:
      return "text-gray-600"
  }
}

export default function AccountQuoteMiniCard(props: {
  label_quote_process_number: string
  label_quote_reference: string
  label_quote_items: string
  label_quote_status: string
  label_quote_status_new: string
  label_quote_status_responded: string
  label_quote_status_expired: string
  label_quote_status_rejected: string
  label_quote_status_sent: string
  isoLocale: string
  sbLocale: string
  productSkus: string[]
  quote: QuoteRO | QuoteRequestRO
  isQuoteRequest: boolean
  productsAsync?: Promise<(ReducedProductData | undefined)[]>
  products?: ReducedProductData[]
  onRemove?: (quote: QuoteRO | QuoteRequestRO, isQuoteRequest: boolean) => void
}) {
  const status = getQuoteStatus(props.quote, props.isQuoteRequest)
  const statusColor = getStatusColor(status)
  const getStatusText = (status: QuoteStatus): string => {
    switch (status) {
      case "EXPIRED":
        return props.label_quote_status_expired
      case "NEW":
        return !props.isQuoteRequest ? props.label_quote_status_new : props.label_quote_status_sent
      case "RESPONDED":
        return props.label_quote_status_responded
      case "REJECTED":
        return props.label_quote_status_rejected
    }
  }
  return (
    <div className={"grid grid-cols-[4.25rem_auto_1rem] grid-rows-[7.5rem] gap-x-2 p-2 pt-0 xs:gap-x-4"}>
      <QuadProductImagesPreview
        className={"relative top-1.5"}
        productSkus={props.productSkus}
        products={props.products}
        productsAsync={props.productsAsync}
      />
      <div className={"flex flex-col gap-0.5 text-sm sm:text-base"}>
        <p>
          <span className={"font-light"}>{props.label_quote_process_number}</span>{" "}
          <span className={"text-red-600"}>{props.quote.number}</span>
        </p>
        <p className={"flex flex-row flex-nowrap gap-1"}>
          <span className={"font-light"}>{props.label_quote_reference}</span>{" "}
          <span className={"line-clamp-1 text-ellipsis"}>{props.quote.displayName}</span>
        </p>
        <p>
          {formatDate(props.isoLocale, props.quote?.creationDate, {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </p>
        <p>
          <span className={"font-bold"}>{props.quote?.items?.length ?? ""}</span>{" "}
          <span className={"font-light"}>{props.label_quote_items}</span>
        </p>
        <p>
          <span className={"font-light"}>{props.label_quote_status}</span>{" "}
          <span className={`${statusColor} font-bold`}>{getStatusText(status)}</span>
        </p>
      </div>
      <div className={cn("flex h-full w-fit flex-col justify-center", { "justify-between": props.onRemove })}>
        <div className={"h-8 w-fit overflow-hidden"}>
          <span className={"relative -top-6 inline-block text-[4rem] leading-none font-extralight text-primary"}>
            {"›"}
          </span>
        </div>
        {props.onRemove && (
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              props.onRemove && props.onRemove(props.quote, props.isQuoteRequest)
            }}
            className={"text-gray-600 hover:text-primary"}
          >
            <TrashIcon className={"inline size-5"} />
          </button>
        )}
      </div>
    </div>
  )
}
